import type { AnalyticsMetric, AnalyticsRange, AnalyticsScope, Department } from "./types";

export type UserAccount = {
  id: number;
  email: string;
};

export type LessonThemeGroup = {
  id: string;
  name: string;
  aliases: string[];
};

export type RemoteSettings = {
  analyticsMetric: AnalyticsMetric;
  analyticsRange: AnalyticsRange;
  analyticsScope: AnalyticsScope;
  groupSubDepartments: boolean;
  hourlyWage: string;
  themeMode: "light" | "dark";
};

export type RemoteState = {
  departments: Department[];
  themeGroups: LessonThemeGroup[];
  settings: Partial<RemoteSettings>;
};

export type RemoteCalendar = {
  fileName: string;
  content: string;
  uploadedAt?: string;
};

const API_BASE = "./api";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}/${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {})
    },
    ...options
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(data.error || "The server request failed.");
  return data;
}

export async function getCurrentUser() {
  return request<{ user: UserAccount | null }>("me.php");
}

export async function login(email: string, password: string) {
  return request<{ user: UserAccount }>("login.php", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
}

export async function register(email: string, password: string) {
  return request<{ user: UserAccount }>("register.php", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
}

export async function logout() {
  return request<{ ok: boolean }>("logout.php", { method: "POST" });
}

export async function loadRemoteState() {
  return request<{ state: RemoteState | null }>("state.php");
}

export async function saveRemoteState(state: RemoteState) {
  return request<{ ok: boolean }>("state.php", {
    method: "PUT",
    body: JSON.stringify(state)
  });
}

export async function loadRemoteCalendar() {
  return request<{ calendar: RemoteCalendar | null }>("calendar.php");
}

export async function saveRemoteCalendar(calendar: Pick<RemoteCalendar, "fileName" | "content">) {
  return request<{ ok: boolean }>("calendar.php", {
    method: "PUT",
    body: JSON.stringify(calendar)
  });
}
